import { getAuthToken } from "@dynamic-labs/sdk-react-core";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

import { Env } from "./env";
import { CustomAxiosRequestConfig, handleApiError } from "./api";

export const API_URL = Env.BASE_URL;

/**
 * Canonical map of API error codes used across interceptors and error handlers.
 */
export const apiErrorCode = {
  INVALID_TOKEN: 419,
  ACCOUNT_BLOCKED: 423,
  ACCESS_DENIED: 430,
  SESSION_EXPIRED: 401,
  INTERNAL_ERROR: 500,
  USER_DEACTIVATED: 403,
  INVALID_DYNAMIC_EMAIL: 422,
};

/**
 * Shared map of success codes surfaced by the backend.
 */
export const apiSuccessCode = {
  GET_SUCCESS: 200,
  POST_SUCCESS: 201,
  PUT_SUCCESS: 202,
};

/**
 * Singleton axios instance responsible for all network requests.
 * Ensures every request shares the same base configuration and interceptors.
 */
export const $axios = axios.create({
  baseURL: API_URL,
  timeout: Env.API_TIMEOUT,
  headers: {
    platform: 1,
    "X-API-Key": Env.API_KEY,
    "accept-language": "en",
    accept: "application/json",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    offset: Math.abs(new Date().getTimezoneOffset() * 60 * 1000),
  },
});

$axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig & CustomAxiosRequestConfig) => {
    const dynamicJwtToken = getAuthToken();

    config.headers = config.headers || {};

    // Inject custom per-request headers safely
    if (config.customHeaders) {
      Object.entries(config.customHeaders).forEach(([key, value]) => {
        config.headers.set(key, value);
      });
    }

    // Auth handling
    if (dynamicJwtToken && !config.skipAuth) {
      config.headers.set("Authorization", `Bearer ${dynamicJwtToken}`);
    } else {
      config.headers.delete("Authorization");
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

$axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const config = error.config as CustomAxiosRequestConfig | undefined;
    if (config?.errorCallback) {
      // Bubble errors through the shared handler when consumers provide a callback.
      handleApiError(error, config.errorCallback);
    }
    return Promise.reject(error);
  },
);
